import { motion } from 'motion/react';
import type { ComponentProps } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { QuestionListItem } from './QuestionListItem';
import type { NewQuestion } from '@/types';
import styles from './QuestionList.module.scss';

interface QuestionListProps {
  questions: NewQuestion[];
  selectedIndex: number | null;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onReorder: (questions: NewQuestion[]) => void;
}

export function QuestionList({
  questions,
  selectedIndex,
  onAdd,
  onEdit,
  onDelete,
  onReorder,
}: QuestionListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd: NonNullable<ComponentProps<typeof DndContext>['onDragEnd']> = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = questions.findIndex(
        (q) => q.order_index.toString() === active.id
      );
      const newIndex = questions.findIndex(
        (q) => q.order_index.toString() === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(questions, oldIndex, newIndex);
        onReorder(reordered);
      }
    }
  };

  // Create array of IDs for sortable context
  const itemIds = questions.map((q) => q.order_index.toString());

  return (
    <div className={styles.questionList}>
      <div className={styles.questionList__header}>
        <h2 className={styles.questionList__title}>
          Questions ({questions.length})
        </h2>
        <motion.button
          className={styles.questionList__addButton}
          onClick={onAdd}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          + Add Question
        </motion.button>
      </div>

      {questions.length === 0 ? (
        <motion.div
          className={styles.questionList__empty}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className={styles.questionList__emptyText}>
            No questions yet. Click "Add Question" to get started!
          </p>
        </motion.div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <div className={styles.questionList__items}>
              {questions.map((question, index) => (
                <QuestionListItem
                  key={question.order_index}
                  question={question}
                  index={index}
                  isSelected={selectedIndex === index}
                  onEdit={() => onEdit(index)}
                  onDelete={() => onDelete(index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
